import {UpmEntityType} from '@hconnect/common/types'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {useCreateEntityConfig} from '../../hooks/upmEntityConfig/useCreateEntityConfig'
import {useEntityConfig} from '../../hooks/upmEntityConfig/useEntityConfig'
import {useUpdateEntityConfig} from '../../hooks/upmEntityConfig/useUpdateEntityConfig'
import {
  createDefaultPositions,
  deserializeSetups,
  removeInvalidRefs,
  serializeSetups
} from '../helpers'
import {
  type FlowConfig,
  type FlowEntity,
  type FlowLink,
  type FlowSetup,
  type TerminalNode
} from '../types'

const DEFAULT_CANVAS_WIDTH = 1200

interface UseFlowConfigOptions {
  plantUpmId: string
  entityUpmId: string
  entityType: UpmEntityType
  configName: string
  entities: FlowEntity[]
  terminalNode: TerminalNode
  maxSetups: number
}

interface UseFlowConfigResult {
  config: FlowConfig
  activeSetup: FlowSetup
  selectedSetupId: string
  setSelectedSetupId: (id: string) => void
  isDirty: boolean
  handleSave: () => Promise<void>
  handleRevert: () => void
  handleCreateSetup: () => FlowSetup | undefined
  handleDeleteSetup: (setupId: string) => void
  handleLinkAdded: (link: FlowLink) => void
  handleLinkRemoved: (link: FlowLink) => void
  handleNodeMoved: (nodeId: string, x: number, y: number) => void
  canCreateSetup: boolean
  configNotCreated: boolean
}

export const useFlowConfig = ({
  plantUpmId,
  entityUpmId,
  entityType,
  configName,
  entities,
  terminalNode,
  maxSetups
}: UseFlowConfigOptions): UseFlowConfigResult => {
  const {data: entityConfig} = useEntityConfig(plantUpmId, entityUpmId, entityType)
  const {mutateAsync: createEntityConfig} = useCreateEntityConfig()
  const {mutateAsync: updateEntityConfig} = useUpdateEntityConfig()

  const configNotCreated = !entityConfig

  const [selectedSetupId, setSelectedSetupId] = useState('setup-1')
  const [isDirty, setIsDirty] = useState(false)

  const createDefaultLinks = useCallback(
    (): FlowLink[] =>
      entities.map((entity) => ({sourceNodeId: entity.id, targetNodeId: terminalNode.id})),
    [entities, terminalNode]
  )

  const createDefaultSetup = useCallback(
    (): FlowSetup => ({
      id: 'setup-1',
      links: [],
      positions: createDefaultPositions(entities, terminalNode, DEFAULT_CANVAS_WIDTH)
    }),
    [entities, terminalNode]
  )

  const savedConfig = useMemo<FlowConfig | undefined>(() => {
    if (!entityConfig) return undefined
    const entry = entityConfig.payload.productionData.find((d) => d.name === configName)
    if (!entry) return undefined
    const setups = deserializeSetups(entry.value, terminalNode.id)
    return {setups: removeInvalidRefs(setups, entities, terminalNode.id)}
  }, [entityConfig, configName, terminalNode.id, entities])

  const [config, setConfig] = useState<FlowConfig>(
    () => savedConfig ?? {setups: [createDefaultSetup()]}
  )

  useEffect(() => {
    if (savedConfig && !isDirty) {
      setConfig(savedConfig)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedConfig])

  const activeSetup = useMemo(
    () => config.setups.find((setup) => setup.id === selectedSetupId) ?? config.setups[0],
    [config, selectedSetupId]
  )

  const updateActiveSetup = useCallback(
    (updater: (prev: FlowSetup) => FlowSetup) => {
      setConfig((prev) => ({
        ...prev,
        setups: prev.setups.map((setup) => (setup.id === selectedSetupId ? updater(setup) : setup))
      }))
    },
    [selectedSetupId]
  )

  const handleLinkAdded = useCallback(
    (link: FlowLink) => {
      updateActiveSetup((prev) => ({...prev, links: [...prev.links, link]}))
      setIsDirty(true)
    },
    [updateActiveSetup]
  )

  const handleLinkRemoved = useCallback(
    (link: FlowLink) => {
      updateActiveSetup((prev) => ({
        ...prev,
        links: prev.links.filter(
          (existing) =>
            !(
              existing.sourceNodeId === link.sourceNodeId &&
              existing.targetNodeId === link.targetNodeId
            )
        )
      }))
      setIsDirty(true)
    },
    [updateActiveSetup]
  )

  const handleNodeMoved = useCallback(
    (nodeId: string, x: number, y: number) => {
      updateActiveSetup((prev) => {
        const exists = prev.positions.some((pos) => pos.nodeId === nodeId)
        return {
          ...prev,
          positions: exists
            ? prev.positions.map((pos) => (pos.nodeId === nodeId ? {...pos, x, y} : pos))
            : [...prev.positions, {nodeId, x, y}]
        }
      })
      setIsDirty(true)
    },
    [updateActiveSetup]
  )

  const canCreateSetup = config.setups.length < maxSetups

  const handleCreateSetup = useCallback((): FlowSetup | undefined => {
    if (!canCreateSetup) return undefined
    const maxId = Math.max(...config.setups.map((s) => parseInt(s.id.split('-')[1])))
    const newSetup: FlowSetup = {
      id: `setup-${maxId + 1}`,
      links: createDefaultLinks(),
      positions: createDefaultPositions(entities, terminalNode, DEFAULT_CANVAS_WIDTH)
    }
    setConfig((prev) => ({...prev, setups: [...prev.setups, newSetup]}))
    setSelectedSetupId(newSetup.id)
    setIsDirty(true)
    return newSetup
  }, [canCreateSetup, config.setups, entities, terminalNode, createDefaultLinks])

  const handleDeleteSetup = useCallback((setupId: string) => {
    if (setupId === 'setup-1') return
    setConfig((prev) => ({
      ...prev,
      setups: prev.setups.filter((setup) => setup.id !== setupId)
    }))
    setSelectedSetupId('setup-1')
    setIsDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    const existingData = entityConfig?.payload.productionData ?? []
    const otherData = existingData.filter((d) => d.name !== configName)
    const dto = {
      metadata: {
        configurationType: 'JanusProductionData',
        plantId: plantUpmId,
        upmType: entityType,
        typeUpmId: entityUpmId
      },
      payload: {
        productionData: [
          ...otherData,
          {
            name: configName,
            value: serializeSetups(config.setups, terminalNode.id)
          }
        ]
      }
    }
    const mutation = configNotCreated
      ? createEntityConfig({plantUpmId, entityConfig: dto, type: entityType})
      : updateEntityConfig({plantUpmId, entityConfig: dto, type: entityType})
    await mutation
    setIsDirty(false)
  }, [
    entityConfig,
    config.setups,
    configNotCreated,
    plantUpmId,
    entityUpmId,
    entityType,
    configName,
    terminalNode.id,
    createEntityConfig,
    updateEntityConfig
  ])

  const handleRevert = useCallback(() => {
    setConfig(savedConfig ?? {setups: [createDefaultSetup()]})
    setIsDirty(false)
  }, [savedConfig, createDefaultSetup])

  return {
    config,
    activeSetup,
    selectedSetupId,
    setSelectedSetupId,
    isDirty,
    handleSave,
    handleRevert,
    handleCreateSetup,
    handleDeleteSetup,
    handleLinkAdded,
    handleLinkRemoved,
    handleNodeMoved,
    canCreateSetup,
    configNotCreated
  }
}
